import { Flags } from '@constants/flags';
import { FeatureFlagTypes } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import Search from '@mui/icons-material/Search';
import { InputAdornment, TextField } from '@mui/material';
import { useFlag } from '@onevesthq/feature-flags/web';
import { AccountTypes } from '@onevesthq/ov-enums';
import { Badge } from '@ovComponents/2-component/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Button, Typography, useTheme } from 'hephaestus/web';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import type {
  CreateAccountForGoalArgs,
  CreateAccountForGoalOptions,
} from './createAccountForGoal';

const DEFAULT_ACCOUNT_TYPE_WEIGHT = 15;

const ACCOUNT_TYPE_DISPLAY_WEIGHT: Partial<Record<AccountTypes, number>> = {
  [AccountTypes.PERSONAL]: 1,
  [AccountTypes.CASH_JOINT]: 2,
  [AccountTypes.TFSA]: 3,
  [AccountTypes.RRSP]: 4,
  [AccountTypes.RRSP_SPOUSAL]: 5,
  [AccountTypes.RESP_FAMILY]: 6,
  [AccountTypes.RESP_SINGLE]: 7,
  [AccountTypes.RESP_ADULT]: 8,
  [AccountTypes.RESP_FAMILY_JOINT]: 9,
  [AccountTypes.RESP_SINGLE_JOINT]: 10,
  [AccountTypes.RRIF]: 11,
};

const accountTypesForDisplay = (
  types: AccountTypes[],
  searchQuery: string,
  translate: (key: string) => string,
) => {
  const needle = searchQuery.trim().toUpperCase();
  return types
    .filter((type) => {
      const techHaystack = type.toUpperCase();
      const translatedHaystack = translate(
        `accountTypes:${type}`,
      ).toUpperCase();
      return (
        techHaystack.includes(needle) || translatedHaystack.includes(needle)
      );
    })
    .sort(
      (a, b) =>
        (ACCOUNT_TYPE_DISPLAY_WEIGHT[a] ?? DEFAULT_ACCOUNT_TYPE_WEIGHT) -
        (ACCOUNT_TYPE_DISPLAY_WEIGHT[b] ?? DEFAULT_ACCOUNT_TYPE_WEIGHT),
    );
};

export type CreateAccountForGoalVisualProp = {
  options: CreateAccountForGoalOptions;
  accountType: string;
  continueFunc: (
    createAccountForGoalArgs: CreateAccountForGoalArgs,
  ) => Promise<void>;
  suggestedTypes: AccountTypes[];
  otherTypes: AccountTypes[];
  workflowCompletion?: any;
  loading: boolean;
  saving?: boolean;
  activeAccountsByType?: Record<string, Account[]>;
  getReusableRespTransferValue?: (accountType: string) => boolean;
  willPromptAccountCreationMethodChoice?: (accountType: string) => boolean;
  confirmationDialog?: React.ReactNode;
  accountCreationMethodChoiceDialog?: React.ReactNode;
  repCodeField?: React.ReactNode;
  validateRepCode?: () => boolean;
};

export const CreateAccountForGoalVisual = ({
  options,
  continueFunc,
  accountType,
  suggestedTypes,
  otherTypes,
  workflowCompletion,
  loading,
  saving = false,
  activeAccountsByType: accountsByType = {},
  getReusableRespTransferValue,
  willPromptAccountCreationMethodChoice,
  confirmationDialog,
  accountCreationMethodChoiceDialog,
  repCodeField,
  validateRepCode,
}: CreateAccountForGoalVisualProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation([
    'client',
    'accountTypes',
    'accountsDetail',
    'workflowCompletions',
  ]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedComponent, setSelectedComponent] = useState<
    'SUGGESTED' | 'OTHER' | 'DEFAULT'
  >('DEFAULT');
  const [respDialogType, setRespDialogType] = useState<string | null>(null);
  const [
    isTransferredFromAnotherInstitution,
    setIsTransferredFromAnotherInstitution,
  ] = useState(false);
  const hasCreateAccountOrSubAccountConfig = useOrganizationFeatureFlag(
    FeatureFlagTypes.CreateAccountOrSubAccountConfig,
  );
  const { enabled: isRespInstitutionTransferEnabled } = useFlag(
    Flags.resp_transferred_from_another_institution,
    { default: false },
  );

  const onTileSelect = async (e: { target: { value: AccountTypes } }) => {
    if (loading || saving) return;
    if (validateRepCode && !validateRepCode()) return;
    setSelectedComponent('SUGGESTED');
    sendAnalytic(
      ovAnalyticsEvents.workflowsCreateAccountForGoalContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );
    const selectedAccountType = e.target.value;
    if (
      isRespInstitutionTransferEnabled &&
      String(selectedAccountType).includes('RESP') &&
      // Skip the standalone dialog when the account-creation-method dialog will handle the
      // transfer toggle; there the account isn't chosen yet, so pre-filling here would be
      // ambiguous and could clobber an existing account's flag on confirm.
      !willPromptAccountCreationMethodChoice?.(selectedAccountType)
    ) {
      // Pre-fill with the value already persisted on the account being reused so the user
      // sees the current DB state rather than a hard-coded default.
      setIsTransferredFromAnotherInstitution(
        getReusableRespTransferValue?.(selectedAccountType) ?? false,
      );
      setRespDialogType(selectedAccountType);
      return;
    }
    await continueFunc({ accountType: selectedAccountType });
  };

  const confirmRespAccountCreation = async () => {
    const selectedAccountType = respDialogType;
    setRespDialogType(null);
    if (!selectedAccountType) return;
    await continueFunc({
      accountType: selectedAccountType as AccountTypes,
      isTransferredFromAnotherInstitution,
    });
  };

  const searchQueryExists = searchQuery.trim() !== '';
  const suggestedList = accountTypesForDisplay(suggestedTypes, searchQuery, t);
  const otherList = accountTypesForDisplay(otherTypes, searchQuery, t);

  const asValueLabelSubtitle = (type: string) => {
    const existingAccountsCount = accountsByType[type]?.length || 0;
    return {
      value: type,
      label: t(`accountTypes:${type}`),
      subtitle: options.displayAccountDefinitions
        ? t(`accountsDetail:accountDescriptions.${type}`)
        : undefined,
      labelSideComponent: existingAccountsCount > 0 &&
        hasCreateAccountOrSubAccountConfig && (
          <Badge
            label={t(
              'workflowCompletions:createAccountForGoal.numAccountsBadge',
              { numAccounts: existingAccountsCount },
            )}
          />
        ),
    };
  };

  return (
    <Form>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackendString(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackendString(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      {!loading && (
        <>
          {repCodeField}
          <TextField
            value={searchQuery}
            data-testid='search-accounts'
            label=''
            fullWidth
            size='small'
            placeholder={t('accountsDetail:searchAccounts')}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'data-testid': 'fulltextsearch-select' }}
            sx={{ mb: 2 }}
            disabled={saving}
          />

          {!searchQueryExists && suggestedList.length > 0 && (
            <Typography variant='headingSmall' sx={{ mb: 3, fontWeight: 900 }}>
              {translateBackendString(options.commonTypesLabel)}
            </Typography>
          )}
          <SelectionTile
            testId={'account-type-suggested'}
            onChange={onTileSelect}
            value={accountType}
            options={suggestedList.map(asValueLabelSubtitle)}
            type='completion'
            selectedComponent={selectedComponent}
            component='SUGGESTED'
            disabled={saving}
          />

          {!searchQueryExists && suggestedList.length > 0 && (
            <Typography
              variant='headingSmall'
              sx={{ mb: 3, mt: 3, fontWeight: 900 }}
            >
              {t('accountsDetail:otherAccountTypes')}
            </Typography>
          )}
          <SelectionTile
            testId='account-type'
            onChange={onTileSelect}
            value={accountType}
            options={otherList.map(asValueLabelSubtitle)}
            type='completion'
            selectedComponent={selectedComponent}
            component='OTHER'
            disabled={saving}
          />

          {suggestedList.length === 0 && otherList.length === 0 && (
            <Typography
              variant='bodyLarge'
              sx={{ color: theme.color.onSurfaceVariant }}
            >
              {t('accountsDetail:noAccountOptions')}
            </Typography>
          )}
        </>
      )}
      {confirmationDialog}
      {accountCreationMethodChoiceDialog}
      <Dialog
        open={!!respDialogType}
        onClose={() => setRespDialogType(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle onClose={() => setRespDialogType(null)}>
          {t('workflowCompletions:respAccountCreation.transferDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t(
              'workflowCompletions:respAccountCreation.transferDialogAccountTypeLabel',
            )}
            value={respDialogType ? t(`accountTypes:${respDialogType}`) : ''}
            fullWidth
            size='small'
            InputProps={{ readOnly: true }}
            sx={{ mb: 3 }}
          />
          <SwitchField
            data-testid='resp-transferred-from-another-institution'
            label={t(
              'workflowCompletions:respAccountCreation.transferredFromAnotherInstitution',
            )}
            checked={isTransferredFromAnotherInstitution}
            onChange={setIsTransferredFromAnotherInstitution}
          />
        </DialogContent>
        <DialogFooter>
          <Button
            data-testid='resp-create-new-account'
            fullWidth
            loading={saving}
            disabled={saving}
            onClick={confirmRespAccountCreation}
          >
            {t('workflowCompletions:respAccountCreation.createNewAccount')}
          </Button>
        </DialogFooter>
      </Dialog>
    </Form>
  );
};

export default CreateAccountForGoalVisual;
